import { getConfig, klinikenApi } from "@/api";
import { logDebug } from "@/components/DebugLog/helpers";
import { BaseActionsProps } from "@/store/modules/helpers/baseState";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";

export type Sambi = {
    access_token: {
        ticket: string;
        validity_start: string;
        validity_end: string;
    };
    refresh_token: {
        ticket: string;
        validity_start: string;
        validity_end: string;
    };
};

type State = {
    data: {
        sambi?: Sambi;
    };
};

let sambiTimeout: number | undefined;

const getDefaultState: () => State = () => {
    return {
        data: {},
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        sambi: (state: State) => {
            return state.data.sambi;
        },
    },
    mutations: {
        reset: (state: State) => {
            Object.assign(state, getDefaultState());
            clearTimeout(sambiTimeout);
        },
        addSambi: (state: State, { data }: { data: Sambi }) => {
            state.data = {
                ...cloneDeep(state.data),
                sambi: data,
            };
        },
        updateSambi: (state: State, { data }: { data: Partial<Sambi> }) => {
            state.data = {
                ...cloneDeep(state.data),
                sambi: {
                    ...cloneDeep(state.data.sambi),
                    ...(Object.fromEntries(
                        Object.entries(data).filter(
                            ([, value]) => value.ticket && value.validity_start && value.validity_end
                        )
                    ) as Sambi),
                },
            };
        },
        removeSambi: (state: State) => {
            const { sambi, ...otherData } = cloneDeep(state.data);

            state.data = otherData as State["data"];
        },
    },
    actions: {
        addSambi: ({ commit, dispatch }: BaseActionsProps<State>, { data }: { data: Sambi }) => {
            commit("addSambi", { data });
            dispatch("startSambiRefresh");
        },
        removeSambi: async ({ dispatch, commit }: BaseActionsProps<State>) => {
            await dispatch("stopSambiRefresh");
            commit("removeSambi");
        },
        /**
         * Kollar och anropar BE för att få en uppdaterad access token baserat på refresh token.
         *
         * Tar bort lagrade sambi tokens om refresh token inte längre är giltig
         */
        refreshSambi: async ({
            state,
            commit,
            dispatch,
        }: BaseActionsProps<State>): Promise<
            | { error: { type: "undefined" | "error" | "invalid"; errorObject?: Error } }
            | { status: "valid" | "refreshed" }
        > => {
            if (!state.data.sambi?.refresh_token || !state.data.sambi?.access_token) {
                return {
                    error: { type: "undefined" },
                };
            }

            /**
             * Om refresh token inte längre är valid. Då tas lagrade tokens bort
             */
            if (moment().isSameOrAfter(moment(state.data.sambi.refresh_token.validity_end))) {
                await dispatch("removeSambi");

                return {
                    error: {
                        type: "invalid",
                    },
                };
            }

            /**
             * Om nu är innan slutet på validity minus 10 minuter. Då kommer den inte göra ett anrop mot BE.
             * Token gäller i 60 minuter
             */
            if (moment().isBefore(moment(state.data.sambi.access_token.validity_end).subtract(10, "minute"))) {
                return { status: "valid" };
            }

            try {
                const { data } = await klinikenApi.post<Sambi["access_token"]>(
                    "nll/sambi/refresh",
                    { refresh_token: state.data.sambi.refresh_token.ticket },
                    getConfig({})
                );

                commit("updateSambi", { data: { access_token: data } });

                return { status: "refreshed" };
            } catch (error) {
                console.error(error);

                return { error: { type: "error", errorObject: error as Error } };
            }
        },
        /**
         * Startar rekursivt anrop som kollar om access token behöver refreshas
         */
        startSambiRefresh: ({ dispatch, state }: BaseActionsProps<State>) => {
            if (!state.data.sambi) {
                return;
            }

            sambiTimeout = setTimeout(async () => {
                const result = (await dispatch("refreshSambi")) as {
                    status?: string;
                    error?: { type: string; errorObject?: Error };
                };

                logDebug({
                    message: {
                        content: (result.status ?? result.error?.type) || "",
                        type: result.status ? (result.status === "valid" ? "info" : "success") : "error",
                        source: "Sambi refresh",
                    },
                });

                if (["invalid", "undefined"].includes(result.error?.type ?? "")) {
                    return;
                }

                dispatch("startSambiRefresh");
            }, 10000) as unknown as number;
        },
        stopSambiRefresh: () => {
            clearTimeout(sambiTimeout);
        },
    },
};
